import { Box, Flex } from '@mantine/core';
import type { Communication } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useMediaQuery } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ComposeNewPanel, InboxSidebar, ThreadView } from './components';
import { useMessagingThreads } from './hooks/useMessagingThreads';
import { notifyUnreadStateChanged } from './hooks/useUnreadThreadCount';
import { markThreadRootRead } from './services/messagingFhir';
import { resolveMessagingThreadSelection } from './resolveMessagingThreadSelection';
import { isMessagingParticipantProfile, resolveMessagingCurrentUserRef } from './services/messagingIdentity';
import { useDisplayTimeZone } from '../../hooks/usePreferredDisplayTimeZone';

function getMessagingBasePath(pathname: string): string | null {
  if (pathname.startsWith('/client-portal/messages')) return '/client-portal/messages';
  if (pathname.startsWith('/dashboard/messages')) return '/dashboard/messages';
  return null;
}

/**
 * MessagingPage - Main messaging/chat interface
 *
 * Features:
 * - Unified inbox: All | Clients | Providers | Spaces
 * - Per-message threading (Start thread / View N replies) like Google Workspaces
 * - Thread view with back arrow, parent message at top, replies, compose
 * - Members drawer (always labeled Members)
 */
export interface MessagingPageProps {
  embedded?: boolean;
  height?: string;
  inboxSections?: Array<'all' | 'clients' | 'providers' | 'spaces'>;
  composeRecipientTypes?: Array<'client' | 'provider' | 'space'>;
  extraThreads?: Communication[];
  initialThreadId?: string;
  initialMessages?: Communication[];
}

export function MessagingPage({ embedded, height, inboxSections, composeRecipientTypes, extraThreads, initialThreadId, initialMessages }: MessagingPageProps): JSX.Element {
  const { threadId: urlThreadId } = useParams<{ threadId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scrollToMessageId = searchParams.get('messageId') ?? undefined;
  const navState = (location.state ?? {}) as { selectThreadId?: string };
  const resolvedInitialThreadId = resolveMessagingThreadSelection({
    initialThreadId,
    selectThreadId: navState.selectThreadId,
    urlThreadId,
  });
  const [selectedThread, setSelectedThread] = useState<string | null>(resolvedInitialThreadId);
  const [selectedParentMessageId, setSelectedParentMessageId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  /** Newly created or mock-updated thread roots so the sidebar updates without a full page reload. */
  const [localExtraThreads, setLocalExtraThreads] = useState<Communication[]>([]);

  const mergedExtraThreads = useMemo(
    () => [...(extraThreads ?? []), ...localExtraThreads],
    [extraThreads, localExtraThreads],
  );

  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const displayTimeZone = useDisplayTimeZone();
  const isMobile = useMediaQuery('(max-width: 48em)') ?? false;
  /** Must match `resolveMessagingCurrentUserRef` used by the inbox (mock remaps e.g. `Practitioner/123`). */
  const readerReference = useMemo(() => {
    if (!profile?.id) return undefined;
    if (!isMessagingParticipantProfile(profile)) return undefined;
    return resolveMessagingCurrentUserRef(profile, false).reference;
  }, [profile]);
  const { threads, refetch: refetchThreads } = useMessagingThreads(mergedExtraThreads);

  // Ref avoids putting `threads` in handleThreadViewed deps, which would create
  // an infinite mark-read → refetch → callback-identity-change → re-fire loop.
  const threadsRef = useRef(threads);
  useEffect(() => { threadsRef.current = threads; }, [threads]);

  useEffect(() => {
    if (initialThreadId) {
      setSelectedThread(initialThreadId);
    }
  }, [initialThreadId]);

  useEffect(() => {
    if (navState.selectThreadId) {
      setSelectedThread(navState.selectThreadId);
    } else if (urlThreadId) {
      setSelectedThread(urlThreadId);
    } else if (isMobile && getMessagingBasePath(location.pathname)) {
      setSelectedThread(null);
    }
    if (
      navState.selectThreadId &&
      !urlThreadId &&
      !embedded &&
      location.pathname.startsWith('/client-portal/messages')
    ) {
      navigate(`/client-portal/messages/${navState.selectThreadId}`, { replace: true, state: {} });
    }
  }, [navState.selectThreadId, urlThreadId, isMobile, navigate, embedded, location.pathname]);

  const syncMessagingUrl = useCallback((threadId: string | null) => {
    if (embedded || !isMobile) return;
    const base = getMessagingBasePath(location.pathname);
    if (!base) return;
    navigate(threadId ? `${base}/${threadId}` : base);
  }, [embedded, isMobile, location.pathname, navigate]);

  const handleComposeNew = (): void => {
    setComposing(true);
    setSelectedThread(null);
    setSelectedParentMessageId(null);
  };

  const handleSelectThread = useCallback((conversationId: string, parentMessageId?: string | null) => {
    setSelectedThread(conversationId);
    setSelectedParentMessageId(parentMessageId ?? null);
    setComposing(false);
    syncMessagingUrl(conversationId);
  }, [syncMessagingUrl]);

  const handleOpenThread = useCallback((conversationId: string, parentMessageId: string) => {
    setSelectedThread(conversationId);
    setSelectedParentMessageId(parentMessageId);
  }, []);

  const handleBackToMain = useCallback(() => {
    setSelectedParentMessageId(null);
  }, []);

  const handleThreadCreated = useCallback((thread: Communication) => {
    if (thread.id) {
      setSelectedThread(thread.id);
      setLocalExtraThreads((prev) => [thread, ...prev.filter((t) => t.id !== thread.id)]);
      syncMessagingUrl(thread.id);
    }
    setSelectedParentMessageId(null);
    setComposing(false);
  }, [syncMessagingUrl]);

  const handleCloseCompose = useCallback(() => {
    setComposing(false);
  }, []);

  const handleBackToInbox = useCallback(() => {
    setSelectedThread(null);
    setSelectedParentMessageId(null);
    setComposing(false);
    syncMessagingUrl(null);
  }, [syncMessagingUrl]);

  const showInbox = !isMobile || (!selectedThread && !composing);
  const showThreadPane = !isMobile || Boolean(selectedThread) || composing;
  const inboxFullWidth = isMobile && showInbox && !showThreadPane;

  const handleThreadViewed = useCallback(
    (threadId: string) => {
      if (readerReference) {
        void markThreadRootRead(medplum, threadId, readerReference).then((ok) => {
          if (ok) {
            void refetchThreads();
            notifyUnreadStateChanged();
          }
        });
      }
    },
    [medplum, readerReference, refetchThreads],
  );

  return (
    <Box
      style={{
        margin: embedded ? 0 : 'calc(var(--mantine-spacing-md) * -1)',
        width: embedded ? '100%' : 'calc(100% + var(--mantine-spacing-md) * 2)',
      }}
    >
      <Flex h={height ?? (embedded ? '100%' : 'calc(100vh - 92px)')} style={{ overflow: 'hidden' }}>
        {showInbox ? (
          <InboxSidebar
            selectedThread={selectedThread}
            onSelectThread={handleSelectThread}
            onComposeNew={handleComposeNew}
            sections={inboxSections}
            threads={threads}
            fullWidth={inboxFullWidth}
            displayTimeZone={displayTimeZone}
            localhostOnly={!embedded && location.pathname.startsWith('/dashboard/messages')}
          />
        ) : null}

        {showThreadPane ? (
          <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {composing ? (
              <ComposeNewPanel
                allowedRecipientTypes={composeRecipientTypes}
                onThreadCreated={handleThreadCreated}
                onClose={handleCloseCompose}
              />
            ) : (
              <ThreadView
                threadId={selectedThread}
                parentMessageId={selectedParentMessageId}
                onBackToMain={handleBackToMain}
                onBackToInbox={isMobile ? handleBackToInbox : undefined}
                onOpenThread={handleOpenThread}
                extraThreads={mergedExtraThreads}
                initialMessages={initialMessages}
                initialScrollToMessageId={scrollToMessageId}
                onAfterMessageSent={() => void refetchThreads()}
                onThreadViewed={handleThreadViewed}
                displayTimeZone={displayTimeZone}
              />
            )}
          </Box>
        ) : null}
      </Flex>
    </Box>
  );
}

